import { useSelector, useDispatch } from "react-redux";
import { useMemo } from "react";
import { useState, useEffect } from "react";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import { addOrUpdateRoleAction, getModuleListAction } from "../../../store/actions/RoleAction";
import Loader from "../Common/Loader";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
const AddRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const roleData = location.state?.role || null;
  const { id } = useParams();
  const modulesData = useSelector((state) => state.role.modules || []);

  const [selected, setSelected] = useState({});
  const initializeSubmodules = () => {
    const parents = modulesData.filter((m) => String(m.sub_module_id) === "0");
    const submodules = {};
    parents.forEach((parent) => {
      submodules[parent.module_name] = [];
    });
    return submodules;
  };

  const [selectedSubmodules, setSelectedSubmodules] = useState({});


  const [role, setRole] = useState({
    role_id: "",
    role_name: "",
    role_description: "",
    role_status: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    dispatch(getModuleListAction());
  }, [dispatch]);

  useEffect(() => {
    if (modulesData.length > 0 && !id) {
      setSelectedSubmodules(initializeSubmodules());
    }
  }, [modulesData, id]);

  useEffect(() => {
    if (id && roleData && modulesData.length > 0) {
      setFetching(true);
      setError("");
      const moduleIdsFromData = (roleData.modules || []).map((mod) => String(mod.module_id));
      const newSelected = {};

      groupedModules.forEach((parent) => {
        if (moduleIdsFromData.includes(String(parent.module_id))) {
          newSelected[parent.module_id] = true;
        }

        parent.children.forEach((child) => {
          if (moduleIdsFromData.includes(String(child.module_id))) {
            newSelected[child.module_id] = true;
            newSelected[parent.module_id] = true;
          }
        });
      });

      setSelected(newSelected);
      setRole({
        role_id: roleData.role_id,
        role_name: roleData.role_name,
        role_description: roleData.role_description,
        role_status: roleData.role_status,
      });

      setSelectedSubmodules({
        ...initializeSubmodules(),
        ...roleData.role_submodules,
      });
      setFetching(false);
    }
  }, [id, roleData, modulesData]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setRole((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeSelection = (id) => {
    setSelected((prev) => {
      const newSelected = { ...prev };
      const isSelected = !!newSelected[id];
      newSelected[id] = !isSelected;

      // If parent is toggled, toggle its children
      const parent = groupedModules.find((mod) => mod.module_id === id);
      if (parent) {
        parent.children.forEach((child) => {
          newSelected[child.module_id] = !isSelected;
        });
      }

      // If child is toggled, check if all siblings are deselected and deselect parent if needed
      groupedModules.forEach((parentMod) => {
        const isChild = parentMod.children.find((child) => child.module_id === id);
        if (isChild) {
          const allUnchecked = parentMod.children.every(
            (child) => !newSelected[child.module_id]
          );
          newSelected[parentMod.module_id] = !allUnchecked;
        }
      });

      return newSelected;
    });
  };

  const handleSubmoduleCheckboxChange = (module, submodule) => {
    setSelectedSubmodules((prev) => {
      const current = prev[module] || [];
      const updated = current.includes(submodule)
        ? current.filter((s) => s !== submodule)
        : [...current, submodule];
      return { ...prev, [module]: updated };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!role.role_name.trim()) newErrors.role_name = "Please Enter Role name";

    const hasSelectedModule = Object.values(selected).some((val) => val === true);
    if (!hasSelectedModule) {
      newErrors.role_modules = "Please select at least one module";
    }
    if (!role.role_status) newErrors.role_status = "Status is required";

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      const selectedModuleIds = Object.keys(selected).filter((id) => selected[id]);

      const payload = {
        role_name: role.role_name,
        role_description: role.role_description,
        role_modules: selectedModuleIds.join("|"),
        role_submodules: selectedSubmodules,
        role_status: role.role_status,
        ...(role.role_id && { role_id: role.role_id }),
      };

      await dispatch(addOrUpdateRoleAction(payload));
      navigate("/roles");
    } catch (err) {
      console.error("Error while saving role:", err);
      setError("Failed to save role. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const groupModules = () => {
    const parents = modulesData.filter((m) => String(m.sub_module_id) === "0");
    const children = modulesData.filter((m) => String(m.sub_module_id) !== "0");

    return parents.map((parent) => ({
      ...parent,
      children: children.filter((child) => String(child.sub_module_id) === String(parent.module_id)),
    }));
  };

  const groupedModules = useMemo(() => groupModules(), [modulesData]);

  if (fetching) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
              <Link to="/roles">
                <Button variant="primary">Back</Button>
              </Link>
            </div>
            {/* {error && <div className="text-danger mb-2">{error}</div>}
            {loading && (
              <div className="text-center mb-3">
                <Loader size="md" />
              </div>
            )} */}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-12 mb-3">
                  <label className="form-label">Role Name
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="role_name"
                    value={role.role_name}
                    onChange={handleChange}
                    placeholder="Enter Role Name"
                  // required
                  />
                  {error.role_name && <small className="text-danger">{error.role_name}</small>}
                </div>

                <div className="col-xl-12 mb-3">
                  <label className="form-label">Role Description</label>
                  <textarea
                    className="form-control"
                    name="role_description"
                    value={role.role_description}
                    onChange={handleChange}
                    placeholder="Enter Role Description"
                  //required
                  />
                </div>

                <div className="col-xl-12 mb-3">
                  <label className="form-label"><strong>Modules</strong></label>
                  <span style={{ color: "red" }}>*</span>
                  <div>
                    {groupedModules.map((parent) => (
                      <div key={parent.module_id} style={{ marginBottom: '1em' }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={!!selected[parent.module_id]}
                            onChange={() => handleChangeSelection(parent.module_id)}
                          />
                          <strong style={{ marginLeft: "8px" }}>{parent.module_name}</strong>
                        </label>
                        <div style={{ marginLeft: '1.5em', marginTop: '0.5em' }}>
                          {parent.children.map((child) => (
                            <div key={child.module_id}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={!!selected[child.module_id]}
                                  onChange={() => handleChangeSelection(child.module_id)}
                                />
                                <span style={{ marginLeft: "8px" }}>{child.module_name}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {error.role_modules && (
                    <small className="text-danger d-block mb-2">{error.role_modules}</small>
                  )}
                </div>

                <div className="col-xl-12 mb-3">
                  <label className="form-label">Status</label>
                  <span style={{ color: "red" }}>*</span>
                  <Select
                    name="role_status"
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                    value={role.role_status ? { value: role.role_status, label: role.role_status } : null}
                    onChange={(option) =>
                      handleChange({
                        target: {
                          name: "role_status",
                          value: option ? option.value : "",
                        },
                      })
                    }
                    placeholder="Select Status"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                    }}
                    isClearable
                  />

                  {error.role_status && <small className="text-danger">{error.role_status}</small>}
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading || fetching}>
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">Submitting...</span>
                  </>
                ) : (
                  <>{id ? "Update Role" : "Add Role"}</>
                )}
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRole;
